import clsx from 'clsx';
import React, { useEffect, useState } from 'react';
import Button from 'react-bootstrap/Button';
import OverlayTrigger from 'react-bootstrap/OverlayTrigger';
import { BsXLg } from 'react-icons/bs';

import type { CommonProps, LoadingState } from '../types/common';

import './BayAlphabeticalFilter.scss';

const letters = 'ABCDEFGHIJKLMNOPQRSTUVWXYZ'.split('');

const fieldToggleStateClasses = {
	active: 'btn-outline-primary bg-blue-50 text-primary fw-light',
	inactive: 'btn-light border-0 fw-light'
};

type FieldValue = 'firstName' | 'lastName';

interface AlphabetFilter {
	AlphabeticFilterField: FieldValue;
	AlphabeticFilterLetter: string | null;
}

// Translation function that supports interpolation
type TranslationFn = (key: string, params?: Record<string, any>) => string;

export interface BayAlphabeticalFilterProps extends Omit<CommonProps, 't'>, LoadingState {
	/** Callback when filter is applied */
	onApplyAlphabetFilter: (filter: AlphabetFilter) => void;
	/** Callback when saving filter to view */
	onSaveView?: (filter: AlphabetFilter) => void;
	/** Currently selected field value */
	selectedFieldValue?: FieldValue | string;
	/** Currently selected letter */
	selectedLetterValue?: string;
	/** Translation function with interpolation support */
	t?: TranslationFn;
}

const BayAlphabeticalFilter: React.FC<BayAlphabeticalFilterProps> = ({
	onApplyAlphabetFilter,
	onSaveView,
	selectedFieldValue = '',
	selectedLetterValue = '',
	isLoading = false,
	t = (key) => key
}) => {
	const [selectedField, setSelectedField] = useState<FieldValue>('firstName');
	const [selectedLetter, setSelectedLetter] = useState<string | null>(null);
	const [alphabetFilterIndicator, setAlphabetFilterIndicator] = useState('A-Z');
	const [alphabetFilterDescription, setAlphabetFilterDescription] = useState('');
	const [show, setShow] = useState(false);

	useEffect(() => {
		const normalizedField: FieldValue = selectedFieldValue === 'lastName' ? 'lastName' : 'firstName';
		const incomingLetter = selectedLetterValue ? String(selectedLetterValue).toUpperCase() : '';
		const normalizedLetter = letters.includes(incomingLetter) ? incomingLetter : null;

		setSelectedField(normalizedField);
		setSelectedLetter(normalizedLetter);
		updateAlphabeticalFilterUI(normalizedField, normalizedLetter);
	}, [selectedFieldValue, selectedLetterValue]);

	const handleReset = () => {
		const filter: AlphabetFilter = {
			AlphabeticFilterField: 'firstName',
			AlphabeticFilterLetter: null
		};

		setSelectedLetter(null);
		setSelectedField('firstName');
		onApplyAlphabetFilter(filter);
		setAlphabetFilterIndicator('A-Z');
		setAlphabetFilterDescription('');
	};

	// Persist current filter selection when user clicks "Save to View"
	const handleSave = () => {
		const filter: AlphabetFilter = {
			AlphabeticFilterField: selectedField,
			AlphabeticFilterLetter: selectedLetter
		};

		if (onSaveView) {
			onSaveView(filter);
		}
		setShow(false);
	};

	// Updates button label and description based on selected field and letter
	const updateAlphabeticalFilterUI = (field: FieldValue, letter: string | null) => {
		if (!letter) {
			setAlphabetFilterIndicator('A-Z');
			setAlphabetFilterDescription('');
			return;
		}

		const indicator = t('alphabeticFilterButtonText', {field: t(field === 'firstName' ? 'firstName' : 'lastName') , letter });
		const description = t('alphabeticFilterIndicationText', {field: t(field === 'firstName' ? 'firstNames' : 'lastNames') , letter });

		setAlphabetFilterIndicator(indicator);
		setAlphabetFilterDescription(description);
	};

	// Renders the popover UI for alphabetical filtering
	const getOverlayContent = (props: any) => {
		const { show, ...safeProps } = props; // Extract 'show' to prevent passing it to the div
		return (
			<div
				className="bg-white rounded-2 border mt-1 alphabetFilter shadow"
				{...safeProps}
				style={{ ...safeProps.style }}
			>
				<div className="p-3">
					<div className="d-flex justify-content-between">
						<div className="d-flex align-items-center mb-2 mb-sm-0">
							<span className="me-2 text-muted filterBy">{t('filterBy')}: </span>

							<button
								className={clsx(
									'btn py-lg-2 px-lg-3',
									selectedField === 'firstName' ? fieldToggleStateClasses.active : fieldToggleStateClasses.inactive
								)}
								type="button"
								onClick={() => {
									setSelectedField('firstName');
								}}
							>
								{t('firstname')}
							</button>

							<button
								className={clsx(
									'btn ms-2 py-lg-2 px-lg-3',
									selectedField === 'lastName' ? fieldToggleStateClasses.active : fieldToggleStateClasses.inactive
								)}
								type="button"
								onClick={() => {
									setSelectedField('lastName');
								}}
							>
								{t('lastname')}
							</button>
							{selectedLetter && (
								<span className="d-none d-sm-block vertical-divider border mx-3" />
							)}

							<div className="d-none d-sm-flex align-items-center text-muted">
								{alphabetFilterDescription}
							</div>
						</div>

						<div className="d-sm-flex align-items-sm-center text-muted cursor-pointer" onClick={() => { setShow(false); }}>
							<BsXLg size={16}/>
						</div>
					</div>
					<div className="d-sm-none d-flex align-items-center text-muted mt-2">
						{alphabetFilterDescription}
					</div>
				</div>

				<div className="d-flex flex-wrap px-3 gap-2">
					{/* Alphabet selection buttons (A–Z) */}
					{letters.map((letter) => {
						return (
							<button
								key={letter}
								type="button"
								className={clsx('btn btn-sm rounded-1 bayAlphabetic py-2', {
									'btn-outline-primary bg-blue-50 text-primary fw-light': selectedLetter === letter,
									'border-0 fw-light bg-100': selectedLetter !== letter
								})}
								// Apply filter immediately when a letter is selected
								onClick={() => {
									setSelectedLetter(letter);
									onApplyAlphabetFilter({ AlphabeticFilterField: selectedField, AlphabeticFilterLetter: letter });
									updateAlphabeticalFilterUI(selectedField, letter);
								}}
							>
								{letter}
							</button>
						);
					})}
				</div>
				<hr />

				{/* Footer */}
				<div className="d-flex justify-content-between align-items-center px-3 pb-3">
					<button
						className="btn fw-light border-0"
						disabled={!selectedLetter}
						type="button"
						onClick={handleReset}
					>
						{t('reset')}
					</button>

					<button
						className="btn btn-outline-primary d-none d-sm-inline-block"
						disabled={!selectedLetter}
						type="button"
						onClick={handleSave}
					>
						{t('saveToView')}
					</button>
				</div>
			</div>
		);
	};

	return (
		// Bootstrap overlay trigger controlling popover visibility
		<OverlayTrigger
				rootClose
				overlay={(props) => getOverlayContent(props)}
				placement="bottom-start"
				show={show}
				trigger="click"
				onToggle={(nextShow) => setShow(nextShow)}
			>
				<Button className={clsx('alphabetic-btn cursor-pointer', {
					active: !isLoading && selectedLetter
				})}
					disabled={isLoading}
					variant="outline-secondary"
				>
					{alphabetFilterIndicator}
				</Button>
			</OverlayTrigger>
	);
};

export default BayAlphabeticalFilter;
